import React, { useState, useEffect, useCallback, useRef, ReactNode } from "react";

interface JoystickProps {
  onMove?: (x: number, y: number) => void;
  onRelease?: () => void;
  topChild: ReactNode;
  leftChild: ReactNode;
  bottomChild: ReactNode;
  rightChild: ReactNode;
}

const Joystick: React.FC<JoystickProps> = ({
  onMove,
  onRelease,
  topChild,
  leftChild,
  bottomChild,
  rightChild,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [activePointers, setActivePointers] = useState<
    Map<number, { clientX: number; clientY: number }>
  >(new Map());

  const handleMove = (clientX: number, clientY: number) => {
    if (!isDragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const newX = clientX - rect.left - centerX;
    const newY = clientY - rect.top - centerY;

    const distance = Math.sqrt(newX * newX + newY * newY);
    const maxDistance = 25;

    let normalizedX = newX;
    let normalizedY = newY;

    if (distance > maxDistance) {
      const angle = Math.atan2(newY, newX);
      normalizedX = Math.cos(angle) * maxDistance;
      normalizedY = Math.sin(angle) * maxDistance;
    }

    setPosition({ x: normalizedX, y: normalizedY });
    onMove?.(normalizedX / maxDistance, normalizedY / maxDistance);
  };

  const handleEnd = () => {
    if (!isDragging) return;

    setIsDragging(false);
    setPosition({ x: 0, y: 0 });
    onRelease?.();
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const elem = e.currentTarget;
    elem.setPointerCapture(e.pointerId);
    setActivePointers((prev) =>
      new Map(prev).set(e.pointerId, {
        clientX: e.clientX,
        clientY: e.clientY,
      }),
    );
    setIsDragging(true);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();

    const startPos = activePointers.get(e.pointerId);
    if (!startPos) return;

    handleMove(e.clientX, e.clientY);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    setActivePointers((prev) => {
      const newMap = new Map(prev);
      newMap.delete(e.pointerId);
      if (newMap.size === 0) {
        setIsDragging(false);
        setPosition({ x: 0, y: 0 });
        onRelease?.();
      }
      return newMap;
    });
    if (activePointers.size === 0) {
      handleEnd();
    }
  };

  const handleWindowPointerUp = useCallback((e: PointerEvent) => {
    e.preventDefault();
    setActivePointers((prev) => {
      const newMap = new Map(prev);
      newMap.delete(e.pointerId);
      if (newMap.size === 0) {
        setIsDragging(false);
        setPosition({ x: 0, y: 0 });
        onRelease?.();
      }
      return newMap;
    });
    if (activePointers.size === 0) {
      handleEnd();
    }
  }, [isDragging]);

  useEffect(() => {
    if (!isDragging) return;
    window.addEventListener("pointerup", handleWindowPointerUp);
    return () => {
      window.removeEventListener("pointerup", handleWindowPointerUp);
    };
  }, [isDragging, handlePointerMove, handlePointerUp, handleWindowPointerUp]);

  return (
    <div className="relative">
      <div className="joystick-outer" ref={containerRef}>
        <div className="top-div">{topChild}</div>
        <div className="bottom-div">{bottomChild}</div>
        <div className="left-div">{leftChild}</div>
        <div className="right-div">{rightChild}</div>
        {/* Inner circle */}
        <div
          className="joystick-circle pointer-events-auto cursor-pointer touch-none select-none"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          style={{
            transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
          }}
        ></div>
      </div>
    </div>
  );
};

export default Joystick;
